import { useEffect } from "react";
import type mapboxgl from "mapbox-gl";
import { isMapToolCapturingClicks } from "../../../maps/utils/mapToolInteraction";

const CELL_ID_PATTERN = /^\d+-\d+-\d+$/;

/** Pick a plot cell rather than a field or grid fill stacked under the cursor. */
export const findCellFeature = (
  features: mapboxgl.MapboxGeoJSONFeature[] = []
): mapboxgl.MapboxGeoJSONFeature | undefined =>
  features.find((feature) =>
    CELL_ID_PATTERN.test(String(feature?.properties?.id ?? ""))
  );

export interface UseCellClickParams {
  map: mapboxgl.Map | null;
  enabled: boolean;
  hoverEnabled?: boolean;
  onCellClick: (cellId: string, geometry: GeoJSON.Geometry) => void;
  onCellHover?: (
    cell: { cellId: string; geometry: GeoJSON.Geometry } | null
  ) => void;
}

/**
 * Reused from docs/CWTRC-2268: query the actual rendered cells on map-wide
 * clicks, without depending on the wizard's rebuilt per-cell layer IDs.
 */
const useCellClick = ({
  map,
  enabled,
  hoverEnabled = false,
  onCellClick,
  onCellHover,
}: UseCellClickParams): void => {
  useEffect(() => {
    if (!map || (!enabled && !hoverEnabled)) return;

    const handleClick = (event: mapboxgl.MapMouseEvent): void => {
      if (!enabled || isMapToolCapturingClicks()) return;

      const features = map.queryRenderedFeatures(event.point);
      const hit = findCellFeature(features);
      if (!hit?.properties?.id) return;

      onCellClick(String(hit.properties.id), hit.geometry);
    };

    const handleMove = (event: mapboxgl.MapMouseEvent): void => {
      if (!hoverEnabled || !onCellHover) return;
      const hit = findCellFeature(map.queryRenderedFeatures(event.point));
      onCellHover(
        hit?.properties?.id
          ? { cellId: String(hit.properties.id), geometry: hit.geometry }
          : null
      );
    };

    const handleLeave = (): void => {
      onCellHover?.(null);
    };

    if (enabled) map.on("click", handleClick);
    if (hoverEnabled && onCellHover) {
      map.on("mousemove", handleMove);
      map.on("mouseout", handleLeave);
    }
    return () => {
      if (enabled) map.off("click", handleClick);
      if (hoverEnabled && onCellHover) {
        map.off("mousemove", handleMove);
        map.off("mouseout", handleLeave);
      }
    };
  }, [map, enabled, hoverEnabled, onCellClick, onCellHover]);
};

export default useCellClick;
